var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, Fragment, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { BLACK, BLACK_500, BLACK_600, BLACK_700, BLACK_900, BLUE_500, LILAC_500, tokens } from '../../constants';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { StateConfigContext } from '../../providers';
import { checkIsPlatform, getCurrentEventControlState, getTestId, isValidObject } from '../../utils';
import { Avatar } from '../avatar/Avatar';
import { Checkbox } from '../checkbox/Checkbox';
import { Divider } from '../divider';
import { DynamicSvg } from '../dynamic-svg';
import { InfoTag } from '../info-tag';
import { Input } from '../input';
import { Radio } from '../radio/Radio';
import { Scrollable } from '../scrollable';
import { Typography } from '../typography';
import { getDropdownComponentStyles, getDropDownPosition, getDropdownProps, getDropdownStyles, setRightAvatarStyles } from './DropdownStyles';
var DropdownComponent = forwardRef((props, ref) => {
  var _a;
  var stateContext = useContext(StateConfigContext);
  var dropdownProps = getDropdownProps(props, stateContext);
  var stylesBasedOnState = useRef({});
  var internalRef = useRef();
  var dropdownVisible = useRef();
  var theme = dropdownProps.theme,
    _dropdownProps$testID = dropdownProps.testID,
    testID = _dropdownProps$testID === void 0 ? 'Dropdown' : _dropdownProps$testID,
    _dropdownProps$varian = dropdownProps.variant,
    variant = _dropdownProps$varian === void 0 ? 'none' : _dropdownProps$varian,
    _dropdownProps$dropdo = dropdownProps.dropdownList,
    dropdownList = _dropdownProps$dropdo === void 0 ? [] : _dropdownProps$dropdo,
    placeholder = dropdownProps.placeholder,
    label = dropdownProps.label,
    postfixIconName = dropdownProps.postfixIconName,
    postfixIconAccessibilityProps = dropdownProps.postfixIconAccessibilityProps,
    style = dropdownProps.style,
    inputContainerStyle = dropdownProps.inputContainerStyle,
    variantPosition = dropdownProps.variantPosition,
    controlState = dropdownProps.controlState,
    _dropdownProps$filter = dropdownProps.filterKey,
    filterKey = _dropdownProps$filter === void 0 ? 'heading' : _dropdownProps$filter,
    onSelectedHandler = dropdownProps.onSelectedHandler,
    onSearchText = dropdownProps.onSearchText,
    inputContainerProps = dropdownProps.inputContainerProps,
    _dropdownProps$noData = dropdownProps.noDataText,
    noDataText = _dropdownProps$noData === void 0 ? 'No data available!' : _dropdownProps$noData,
    _dropdownProps$parent = dropdownProps.parentType,
    parentType = _dropdownProps$parent === void 0 ? 'input' : _dropdownProps$parent,
    _dropdownProps$action = dropdownProps.actionIconName,
    actionIconName = _dropdownProps$action === void 0 ? 'MoreIcon' : _dropdownProps$action,
    _dropdownProps$defaul = dropdownProps.defaultSelectedCheckbox,
    defaultSelectedCheckbox = _dropdownProps$defaul === void 0 ? [] : _dropdownProps$defaul,
    _dropdownProps$value = dropdownProps.value,
    value = _dropdownProps$value === void 0 ? '' : _dropdownProps$value,
    dropdownMenuStyles = dropdownProps.dropdownMenuStyles,
    _onFocus = dropdownProps.onFocus,
    disableDropdown = dropdownProps.disableDropdown,
    readonlyOnPress = dropdownProps.readonlyOnPress,
    searchOnFirstChar = dropdownProps.searchOnFirstChar,
    openOnFirstCharOccurence = dropdownProps.openOnFirstCharOccurence,
    _dropdownProps$clearS = dropdownProps.clearSearchOnDropdownListChange,
    clearSearchOnDropdownListChange = _dropdownProps$clearS === void 0 ? true : _dropdownProps$clearS,
    showClearBtn = dropdownProps.showClearBtn,
    _dropdownProps$showSc = dropdownProps.showScrollable,
    showScrollable = _dropdownProps$showSc === void 0 ? true : _dropdownProps$showSc,
    _dropdownProps$clearI = dropdownProps.clearInputOnBlur,
    clearInputOnBlur = _dropdownProps$clearI === void 0 ? false : _dropdownProps$clearI,
    onInputClear = dropdownProps.onInputClear,
    onDropdownToggle = dropdownProps.onDropdownToggle,
    resetDropdown = dropdownProps.resetDropdown,
    resetDropdownToDefault = dropdownProps.resetDropdownToDefault,
    children = dropdownProps.children,
    _dropdownProps$showIn = dropdownProps.showInputInsideDropdownList,
    showInputInsideDropdownList = _dropdownProps$showIn === void 0 ? false : _dropdownProps$showIn,
    _dropdownProps$initSh = dropdownProps.initShowDropdown,
    initShowDropdown = _dropdownProps$initSh === void 0 ? false : _dropdownProps$initSh,
    _dropdownProps$disabl = dropdownProps.disableFilterOnPassedValue,
    disableFilterOnPassedValue = _dropdownProps$disabl === void 0 ? false : _dropdownProps$disabl,
    restProps = __rest(dropdownProps, ["theme", "testID", "variant", "dropdownList", "placeholder", "label", "postfixIconName", "postfixIconAccessibilityProps", "style", "inputContainerStyle", "variantPosition", "controlState", "filterKey", "onSelectedHandler", "onSearchText", "inputContainerProps", "noDataText", "parentType", "actionIconName", "defaultSelectedCheckbox", "value", "dropdownMenuStyles", "onFocus", "disableDropdown", "readonlyOnPress", "searchOnFirstChar", "openOnFirstCharOccurence", "clearSearchOnDropdownListChange", "showClearBtn", "showScrollable", "clearInputOnBlur", "onInputClear", "onDropdownToggle", "resetDropdown", "resetDropdownToDefault", "children", "showInputInsideDropdownList", "initShowDropdown", "disableFilterOnPassedValue"]);
  var _useState = useState(value),
    searchTerm = _useState[0],
    setSearchTerm = _useState[1];
  var _useState2 = useState(initShowDropdown),
    showDropdownMenu = _useState2[0],
    setShowDropdownMenu = _useState2[1];
  var dropdownStyles = getDropdownComponentStyles(controlState, dropdownProps, parentType, showDropdownMenu);
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);
  var _useState3 = useState(null),
    selectedItem = _useState3[0],
    setSelectedItem = _useState3[1];
  var initializeList = (list, selectedKeys) => {
    return list === null || list === void 0 ? void 0 : list.map(item => Object.assign(Object.assign({}, item), {
      isSelected: selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.includes(item[filterKey])
    }));
  };
  var _useState4 = useState(initializeList([...dropdownList], defaultSelectedCheckbox)),
    originalList = _useState4[0],
    setOriginalList = _useState4[1];
  var _useState5 = useState(initializeList([], defaultSelectedCheckbox)),
    filteredDropdownList = _useState5[0],
    setFilteredDropdownList = _useState5[1];
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var shouldShowDropdown = openOnFirstCharOccurence ? searchTerm.length > 0 : true;
  useImperativeHandle(ref, () => Object.assign(Object.assign({}, internalRef === null || internalRef === void 0 ? void 0 : internalRef.current), {
    openDropdown: () => setShowDropdownMenu(true),
    closeDropdown: () => setShowDropdownMenu(false),
    isDropdownOpen: () => showDropdownMenu
  }));
  useEffect(() => {
    if (resetDropdown) {
      setSearchTerm('');
      var resetList = dropdownList.map(item => Object.assign(Object.assign({}, item), {
        isSelected: false
      }));
      setOriginalList([...resetList]);
      setFilteredDropdownList([...resetList]);
    }
  }, [resetDropdown]);
  useEffect(() => {
    if (resetDropdownToDefault) {
      var resetList = dropdownList.map(item => Object.assign(Object.assign({}, item), {
        isSelected: variant === 'checkbox' ? (defaultSelectedCheckbox === null || defaultSelectedCheckbox === void 0 ? void 0 : defaultSelectedCheckbox.includes(item[filterKey])) || false : (variant === 'radio' || variant === 'none') && value ? item.isSelected : false
      }));
      if (variant === 'checkbox') {
        var selectedTerms = resetList.filter(item => item.isSelected).map(item => item[filterKey]).join(', ');
        setSearchTerm(selectedTerms || '');
      } else if ((variant === 'radio' || variant === 'none') && value) {
        setSearchTerm(value);
      } else {
        setSearchTerm('');
      }
      setOriginalList([...resetList]);
      setFilteredDropdownList([...resetList]);
    }
  }, [resetDropdownToDefault]);
  useEffect(() => {
    var _a;
    if (dropdownVisible === null || dropdownVisible === void 0 ? void 0 : dropdownVisible.current) {
      if (!showDropdownMenu) {
        (_a = props === null || props === void 0 ? void 0 : props.onDropdownClosePress) === null || _a === void 0 ? void 0 : _a.call(props);
      }
      onDropdownToggle === null || onDropdownToggle === void 0 ? void 0 : onDropdownToggle({
        isOpen: showDropdownMenu
      });
    }
    dropdownVisible.current = true;
  }, [showDropdownMenu]);
  useEffect(() => {
    checkIsPlatform('web') && document.addEventListener('mousedown', handleClick);
    return () => {
      checkIsPlatform('web') && document.removeEventListener('mousedown', handleClick);
    };
  }, []);
  var handleClick = event => {
    var _a;
    if ((internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) && !((_a = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _a === void 0 ? void 0 : _a.contains(event === null || event === void 0 ? void 0 : event.target))) {
      setShowDropdownMenu(false);
    }
  };
  useEffect(() => {
    if (clearSearchOnDropdownListChange) {
      setSearchTerm('');
    }
    setFilteredDropdownList(dropdownList);
    setOriginalList(dropdownList);
  }, [JSON.stringify(dropdownList)]);
  useEffect(() => {
    setSearchTerm('');
    if (variant == 'checkbox') {
      setFilteredDropdownList(initializeList(dropdownList, defaultSelectedCheckbox));
      setOriginalList(initializeList([...dropdownList], defaultSelectedCheckbox));
      var selectedTerms = originalList.filter(item => item.isSelected).map(item => item[filterKey]).filter(Boolean);
      var finalInputValue = selectedTerms.join(', ');
      setSearchTerm(finalInputValue);
    }
  }, [JSON.stringify(defaultSelectedCheckbox)]);
  useEffect(() => {
    if (variant !== 'checkbox') {
      if ((value === null || value === void 0 ? void 0 : value.length) > 0) {
        if (!(inputContainerProps === null || inputContainerProps === void 0 ? void 0 : inputContainerProps.preventOnChangeText) && (value === null || value === void 0 ? void 0 : value.length) > 0 && !disableFilterOnPassedValue) {
          var list = originalList === null || originalList === void 0 ? void 0 : originalList.filter(item => {
            var _a, _b, _c, _d;
            return searchOnFirstChar ? (_b = (_a = item === null || item === void 0 ? void 0 : item[filterKey]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === null || _b === void 0 ? void 0 : _b.startsWith(value === null || value === void 0 ? void 0 : value.toLowerCase()) : (_d = (_c = item === null || item === void 0 ? void 0 : item[filterKey]) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === null || _d === void 0 ? void 0 : _d.includes(value === null || value === void 0 ? void 0 : value.toLowerCase());
          });
          setFilteredDropdownList([...list]);
        }
        setSearchTerm(value);
      }
    }
  }, [value, variant]);
  var getDropdownRefPressableStyles = useCallback(event => {
    var _controlState = getCurrentEventControlState(event, controlState);
    var styles = getDropdownComponentStyles(_controlState, dropdownProps, parentType, showDropdownMenu);
    stylesBasedOnState.current = styles;
    return [styles.dropdownMenuContainer, style];
  }, [dropdownProps, parentType, showDropdownMenu]);
  var handleSearch = text => {
    if (text.length > 0 && searchTerm.length > 0 && text === searchTerm) return;
    setSearchTerm(text);
    onSearchText === null || onSearchText === void 0 ? void 0 : onSearchText(text);
    if (openOnFirstCharOccurence) {
      setShowDropdownMenu(openOnFirstCharOccurence && text.length > 0);
    }
    if (!(inputContainerProps === null || inputContainerProps === void 0 ? void 0 : inputContainerProps.preventOnChangeText) && (text === null || text === void 0 ? void 0 : text.length) > 0) {
      if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem[filterKey]) === text) {
        setFilteredDropdownList([...originalList]);
      } else {
        var list = originalList === null || originalList === void 0 ? void 0 : originalList.filter(item => {
          var _a, _b, _c, _d;
          return searchOnFirstChar ? (_b = (_a = item === null || item === void 0 ? void 0 : item[filterKey]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === null || _b === void 0 ? void 0 : _b.startsWith(text === null || text === void 0 ? void 0 : text.toLowerCase()) : (_d = (_c = item === null || item === void 0 ? void 0 : item[filterKey]) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === null || _d === void 0 ? void 0 : _d.includes(text === null || text === void 0 ? void 0 : text.toLowerCase());
        });
        setFilteredDropdownList([...list]);
      }
    }
    if ((text === null || text === void 0 ? void 0 : text.length) == 0) {
      setFilteredDropdownList(originalList);
    }
  };
  var renderPrefixIcon = item => {
    var _ref = item || {},
      disabled = _ref.disabled,
      avatarData = _ref.avatarData,
      isSelected = _ref.isSelected;
    switch (variant) {
      case 'avatar':
        return ((avatarData === null || avatarData === void 0 ? void 0 : avatarData.iconName) || (avatarData === null || avatarData === void 0 ? void 0 : avatarData.imageUrl)) && _jsx(View, Object.assign({
          style: dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.variantContainer
        }, {
          children: _jsx(Avatar, Object.assign({}, avatarData, {
            theme: theme,
            disabled: disabled,
            variant: (avatarData === null || avatarData === void 0 ? void 0 : avatarData.variant) || 'icon',
            size: isLargeScreen ? 'md' : 'sm'
          }))
        }));
      case 'radio':
        return _jsx(View, Object.assign({
          style: dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.variantContainer
        }, {
          children: _jsx(Radio, {
            theme: theme,
            disabled: disabled,
            size: "md",
            checked: isSelected,
            onPress: () => handleDropdownChange(item)
          })
        }));
      case 'checkbox':
        return _jsx(View, Object.assign({
          style: dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.variantContainer
        }, {
          children: _jsx(Checkbox, {
            theme: theme,
            disabled: disabled,
            size: "sm",
            onPress: () => handleDropdownChange(item),
            checked: isSelected
          })
        }));
      default:
        return _jsx(_Fragment, {});
    }
  };
  var handleDropdownChange = (selectedItem, index) => {
    var _a, _b;
    var updateSelection = items => {
      return items === null || items === void 0 ? void 0 : items.map(item => {
        var tempItem = Object.assign({}, item);
        if (item[filterKey] === selectedItem[filterKey]) {
          if (variant === 'checkbox') {
            tempItem.isSelected = !item.isSelected;
          } else {
            tempItem.isSelected = true;
            setSelectedItem(tempItem);
          }
        } else if (variant !== 'checkbox') {
          tempItem.isSelected = false;
        }
        return tempItem;
      });
    };
    var updatedOriginalList = updateSelection(originalList);
    if (variant !== 'checkbox') {
      var selectedTerm = (_a = updatedOriginalList.find(item => item.isSelected)) === null || _a === void 0 ? void 0 : _a[filterKey];
      setSearchTerm(selectedTerm || '');
      setShowDropdownMenu(false);
    } else {
      var selectedTerms = (_b = updatedOriginalList === null || updatedOriginalList === void 0 ? void 0 : updatedOriginalList.filter(item => item.isSelected)) === null || _b === void 0 ? void 0 : _b.map(item => item[filterKey]);
      var finalInputValue = selectedTerms === null || selectedTerms === void 0 ? void 0 : selectedTerms.join(', ');
      setSearchTerm(finalInputValue || '');
    }
    setFilteredDropdownList(updatedOriginalList);
    setOriginalList(updatedOriginalList);
    var updatedSelectedList = updatedOriginalList.filter(item => item.isSelected);
    onSelectedHandler === null || onSelectedHandler === void 0 ? void 0 : onSelectedHandler(updatedSelectedList, searchTerm, selectedItem, index);
  };
  var handleSearchText = text => {
    if (variant !== 'checkbox') {
      handleSearch(text);
    }
  };
  var handleBlur = function handleBlur(_evt, _temp) {
    var _ref2 = _temp === void 0 ? {} : _temp,
      _ref2$isDropdownClose = _ref2.isDropdownClosed,
      isDropdownClosed = _ref2$isDropdownClose === void 0 ? false : _ref2$isDropdownClose;
    if (clearInputOnBlur && variant !== 'checkbox' && (!showDropdownMenu || isDropdownClosed)) {
      if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem[filterKey]) !== searchTerm || !searchTerm) {
        setSearchTerm('');
        setOriginalList([...dropdownList]);
        setFilteredDropdownList([...dropdownList]);
        setFilteredDropdownList(originalList);
        onInputClear === null || onInputClear === void 0 ? void 0 : onInputClear();
      }
    }
  };
  var COMPONENT = {
    input: _jsx(Pressable, Object.assign({
      onPress: () => {
        if (showDropdownMenu) {
          handleBlur('', {
            isDropdownClosed: true
          });
        }
        setShowDropdownMenu(!showDropdownMenu);
      }
    }, checkIsPlatform('web') && {
      tabIndex: 1
    }, {
      disabled: disableDropdown || (inputContainerProps === null || inputContainerProps === void 0 ? void 0 : inputContainerProps.isDisabled)
    }, {
      children: _jsx(Input, Object.assign({}, inputContainerProps, {
        theme: theme,
        label: label,
        placeholder: placeholder,
        showAppend: (postfixIconName === null || postfixIconName === void 0 ? void 0 : postfixIconName.length) > 0 ? true : false,
        appendItems: {
          accessibilityProps: postfixIconAccessibilityProps,
          postfixIcon: {
            iconName: postfixIconName,
            isActionable: true
          },
          isPressable: true,
          onPress: () => {
            setShowDropdownMenu(prevShowDropdownMenu => !prevShowDropdownMenu);
          }
        },
        readOnly: variant == 'checkbox' || (inputContainerProps === null || inputContainerProps === void 0 ? void 0 : inputContainerProps.readOnly)
      }, ((inputContainerProps === null || inputContainerProps === void 0 ? void 0 : inputContainerProps.readOnly) || variant == 'checkbox') && {
        readOnlyOnPress: () => {
          readonlyOnPress === null || readonlyOnPress === void 0 ? void 0 : readonlyOnPress();
          setShowDropdownMenu(prevShowDropdownMenu => !prevShowDropdownMenu);
        }
      }, {
        value: searchTerm,
        onChangeText: handleSearchText,
        onFocus: () => {
          setShowDropdownMenu(shouldShowDropdown);
          _onFocus === null || _onFocus === void 0 ? void 0 : _onFocus();
        },
        onBlur: handleBlur,
        style: inputContainerStyle,
        showClearButton: showClearBtn,
        onClearButtonPress: () => {
          setSearchTerm('');
          onSearchText === null || onSearchText === void 0 ? void 0 : onSearchText('');
          setFilteredDropdownList(originalList);
        }
      }, getTestId(testID, 'input')))
    })),
    icon: _jsx(View, Object.assign({
      style: {
        flexDirection: 'row'
      }
    }, getTestId(testID, 'icon'), {
      children: _jsx(Pressable, Object.assign({
        onPress: () => {
          var _a;
          setShowDropdownMenu(prevShowDropdownMenu => !prevShowDropdownMenu);
          (_a = props === null || props === void 0 ? void 0 : props.onActionIconPress) === null || _a === void 0 ? void 0 : _a.call(props);
        }
      }, {
        children: !!actionIconName && _jsx(DynamicSvg, {
          iconName: actionIconName
        })
      }))
    })),
    children: _jsx(_Fragment, {
      children: children
    })
  };
  restProps === null || restProps === void 0 ? true : delete restProps.position;
  return _jsxs(View, Object.assign({}, getTestId(testID), {
    style: [dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.dropdownParentContainer, style]
  }, restProps, {
    ref: internalRef
  }, {
    children: [parentType && COMPONENT[parentType], showDropdownMenu && _jsxs(View, Object.assign({
      style: [dropdownStyles.dropdownContainer, dropdownMenuStyles, getDropDownPosition(dropdownProps, internalRef)]
    }, {
      children: [showInputInsideDropdownList && _jsxs(View, {
        children: [_jsx(View, Object.assign({
          style: [{
            margin: 16
          }]
        }, {
          children: _jsx(Input, Object.assign({
            placeholder: "Search",
            showAppend: true,
            appendItems: {
              postfixIcon: {
                iconName: 'SearchButtonIcon',
                iconColor: BLACK_900
              }
            }
          }, inputContainerProps, {
            theme: theme,
            value: searchTerm,
            onChangeText: handleSearchText
          }))
        })), _jsx(Divider, {
          height: 1,
          color: LILAC_500
        })]
      }), _jsxs(Scrollable, Object.assign({
        theme: theme,
        variant: "flatList",
        withRoundedBorder: true,
        showScrollbar: showScrollable,
        keyboardShouldPersistTaps: "handled",
        style: showDropdownMenu && {
          zIndex: 101,
          maxHeight: (_a = dropdownStyles.dropdownParentContainer) === null || _a === void 0 ? void 0 : _a.maxHeight
        }
      }, {
        children: [filteredDropdownList.length > 0 && filteredDropdownList.map((item, index) => {
          var _ref3 = item || {},
            sectionListTitleIcon = _ref3.sectionListTitleIcon,
            isSectionList = _ref3.isSectionList,
            sectionListTitle = _ref3.sectionListTitle,
            disabled = _ref3.disabled,
            showInfoTag = _ref3.showInfoTag,
            infoTagProps = _ref3.infoTagProps,
            heading = _ref3.heading,
            headingTitle = _ref3.headingTitle,
            subHeading = _ref3.subHeading,
            subHeadingTitle = _ref3.subHeadingTitle,
            leftIconName = _ref3.leftIconName,
            leftIconProps = _ref3.leftIconProps,
            rightIconName = _ref3.rightIconName,
            value = _ref3.value,
            _ref3$sectionListTitl = _ref3.sectionListTitleAccessibilityProps,
            sectionListTitleAccessibilityProps = _ref3$sectionListTitl === void 0 ? {} : _ref3$sectionListTitl,
            _ref3$singleListItemA = _ref3.singleListItemAccessibilityProps,
            singleListItemAccessibilityProps = _ref3$singleListItemA === void 0 ? {} : _ref3$singleListItemA;
          return _jsxs(Fragment, {
            children: [isSectionList && _jsxs(View, Object.assign({
              style: dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.titleSection
            }, sectionListTitleAccessibilityProps, {
              children: [_jsx(Typography, Object.assign({
                textTransform: "uppercase",
                text: sectionListTitle,
                color: BLACK_700,
                type: "title-single-line-" + (isLargeScreen ? 'xl' : 'lg'),
                numberOfLines: 1
              }, getTestId(testID, 'sectionListTitle'))), !!sectionListTitleIcon && _jsx(DynamicSvg, {
                iconName: sectionListTitleIcon,
                fontSize: isLargeScreen ? 20 : 16,
                color: BLUE_500,
                style: {
                  marginLeft: tokens.spacing.SPACING_4
                }
              }), showInfoTag && _jsx(InfoTag, Object.assign({
                theme: theme
              }, infoTagProps, {
                style: (sectionListTitle || sectionListTitleIcon) && {
                  marginLeft: tokens.spacing.SPACING_4
                }
              }, getTestId(testID, 'infoTag')))]
            })), !!(heading || subHeading || value) && _jsx(Pressable, Object.assign({
              disabled: disabled,
              style: getDropdownRefPressableStyles,
              onPress: () => handleDropdownChange(item, index)
            }, getTestId(testID, "dropdownChild_" + index), singleListItemAccessibilityProps, {
              children: () => {
                var _a, _b, _c;
                return _jsx(_Fragment, {
                  children: _jsxs(View, Object.assign({
                    style: (_a = stylesBasedOnState === null || stylesBasedOnState === void 0 ? void 0 : stylesBasedOnState.current) === null || _a === void 0 ? void 0 : _a.dropdownMenuInfo
                  }, {
                    children: [_jsxs(View, Object.assign({
                      style: [(_b = stylesBasedOnState === null || stylesBasedOnState === void 0 ? void 0 : stylesBasedOnState.current) === null || _b === void 0 ? void 0 : _b.dropdownMenuInfoItem, setRightAvatarStyles(item, variant, variantPosition)]
                    }, {
                      children: [variant && renderPrefixIcon(item), _jsxs(View, Object.assign({
                        style: [(_c = stylesBasedOnState === null || stylesBasedOnState === void 0 ? void 0 : stylesBasedOnState.current) === null || _c === void 0 ? void 0 : _c.variantContainer, stylesBasedOnState.current.headingSubheadingContainer, leftIconName && {
                          flexDirection: 'row'
                        }]
                      }, {
                        children: [!!leftIconName && _jsx(DynamicSvg, Object.assign({
                          iconName: leftIconName
                        }, leftIconProps, {
                          style: {
                            marginRight: tokens.spacing.SPACING_12
                          }
                        }, getTestId(testID, 'leftIcon'))), (!!heading || !!subHeading) && _jsxs(View, Object.assign({
                          style: {
                            flex: 1
                          }
                        }, {
                          children: [_jsx(Typography, Object.assign({
                            text: heading,
                            type: "body-single-line-" + (isLargeScreen ? 'md' : 'sm') + "-bold",
                            color: disabled ? BLACK_500 : BLACK_900,
                            numberOfLines: 1,
                            title: headingTitle
                          }, getTestId(testID, 'heading'))), _jsx(Typography, Object.assign({
                            text: subHeading,
                            numberOfLines: 1,
                            type: "body-para-" + (isLargeScreen ? 'sm' : 'xs'),
                            color: disabled ? BLACK_500 : BLACK_700,
                            style: heading && {
                              marginTop: tokens.spacing.SPACING_4
                            },
                            title: subHeadingTitle
                          }, getTestId(testID, 'subHeading')))]
                        }))]
                      }))]
                    })), variantPosition === 'left' && !!rightIconName && _jsx(DynamicSvg, {
                      iconName: rightIconName,
                      color: disabled ? BLACK_500 : BLACK_600,
                      fontSize: isLargeScreen ? 28 : 24
                    })]
                  }))
                });
              }
            }))]
          }, "single_dropdown_item_" + index);
        }), filteredDropdownList.length == 0 && _jsx(View, Object.assign({
          style: [dropdownStyles.noDataContainer]
        }, {
          children: _jsx(Typography, Object.assign({
            type: "body-para-sm-bold",
            text: noDataText
          }, getTestId(testID, 'noDataText')))
        }))]
      })), showDropdownMenu && !checkIsPlatform('web') && _jsx(Pressable, {
        style: dropdownStyles.outerBtnForOutsideClick,
        onPress: () => {
          setShowDropdownMenu(false);
        }
      })]
    }))]
  }));
});
export var Dropdown = forwardRef((props, ref) => {
  var theme = props.theme,
    _props$showChildDropD = props.showChildDropDown,
    showChildDropDown = _props$showChildDropD === void 0 ? false : _props$showChildDropD,
    style = props.style,
    childStyle = props.childStyle,
    childValue = props.childValue,
    value = props.value,
    dropdownList = props.dropdownList,
    filterKey = props.filterKey,
    _props$searchOnParent = props.searchOnParentDropdown,
    searchOnParentDropdown = _props$searchOnParent === void 0 ? false : _props$searchOnParent;
  var _useState6 = useState({}),
    childProps = _useState6[0],
    setChildProps = _useState6[1];
  var _useState7 = useState(''),
    childDefaultValue = _useState7[0],
    setChildDefaultValue = _useState7[1];
  useEffect(() => {
    showChildDropDown && setChildProps({});
  }, [props === null || props === void 0 ? void 0 : props.dropdownList]);
  if (!showChildDropDown) {
    return _jsx(DropdownComponent, Object.assign({}, props, {
      ref: ref
    }));
  }
  useEffect(() => {
    if (value) {
      var selectedItem = dropdownList === null || dropdownList === void 0 ? void 0 : dropdownList.find(_item => (_item === null || _item === void 0 ? void 0 : _item[filterKey]) === value);
      setChildProps(selectedItem ? selectedItem : {
        value
      });
    }
  }, []);
  useEffect(() => {
    setChildDefaultValue(childValue);
  }, [childValue]);
  var styles = getDropdownStyles();
  return _jsxs(View, Object.assign({
    style: styles === null || styles === void 0 ? void 0 : styles.dropdownBody
  }, {
    children: [_jsx(View, Object.assign({
      style: styles === null || styles === void 0 ? void 0 : styles.dropDown
    }, {
      children: _jsx(DropdownComponent, Object.assign({
        parentType: "input",
        placeholder: "Select"
      }, props, {
        style: style,
        ref: ref,
        onSelectedHandler: (updatedSelectedList, searchTerm, item) => {
          if ((item === null || item === void 0 ? void 0 : item.childType) === 'dropdown') {
            setChildProps(Object.assign(Object.assign({}, item), {
              key: new Date().getTime() + ''
            }));
            setChildDefaultValue('');
          } else {
            setChildProps(Object.assign(Object.assign({}, item), {
              value: '',
              key: new Date().getTime() + ''
            }));
            setChildDefaultValue('');
          }
          props === null || props === void 0 ? void 0 : props.onSelectedHandler(updatedSelectedList, searchTerm, item);
        },
        inputContainerStyle: styles === null || styles === void 0 ? void 0 : styles.dropdownContainer,
        inputContainerProps: {
          preventOnChangeText: !searchOnParentDropdown,
          caretHidden: true
        }
      }))
    })), _jsx(View, Object.assign({
      style: styles === null || styles === void 0 ? void 0 : styles.childContainerStyle
    }, {
      children: (childProps === null || childProps === void 0 ? void 0 : childProps.childType) === 'dropdown' ? (_createElement(DropdownComponent, Object.assign({
        onSelectedHandler: (updatedSelectedList, searchTerm, item) => {
          props === null || props === void 0 ? void 0 : props.onSelectedHandler(updatedSelectedList, searchTerm, Object.assign(Object.assign({}, item), {
            parentItem: childProps
          }));
        }
      }, childProps === null || childProps === void 0 ? void 0 : childProps.childDropDownProps, {
        key: childProps === null || childProps === void 0 ? void 0 : childProps.key,
        inputContainerStyle: styles === null || styles === void 0 ? void 0 : styles.childStyle,
        value: childDefaultValue,
        style: childStyle
      }))) : (_createElement(Input, Object.assign({
        theme: theme,
        showAppend: true,
        appendItems: {
          prefixIcon: {
            iconName: 'SearchIcon',
            iconColor: BLACK,
            isActionable: false
          }
        },
        placeholder: "Search",
        onChangeText: text => (props === null || props === void 0 ? void 0 : props.onChangeText) && props.onChangeText(text, {
          parentItem: childProps
        }),
        value: childDefaultValue
      }, childProps === null || childProps === void 0 ? void 0 : childProps.childInputProps, {
        key: childProps === null || childProps === void 0 ? void 0 : childProps.key,
        isDisabled: !isValidObject(childProps),
        containerStyle: [styles === null || styles === void 0 ? void 0 : styles.childContainerStyle, childStyle],
        style: styles === null || styles === void 0 ? void 0 : styles.childStyle
      })))
    }))]
  }));
});